import {
  ChangeEvent,
  DragEvent,
  InputHTMLAttributes,
  MutableRefObject,
  useCallback,
  useId,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Error, FilePresent, UploadFile } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Skeleton,
  Stack,
  Theme,
  Typography,
} from '@mui/material';
import { lighten } from '@mui/system/colorManipulator';

import LoadingButton from '@/components/LoadingButton';

const CardSx = {
  flexGrow: { xs: 1, sm: 0 },
  flexBasis: 160,
} as const;

const CardContentSx = {
  px: 2,
  pt: 1,
  pb: 0,
  '&:last-child': {
    pb: 1,
  },
} as const;

const FriendlyUploadCardAnimation = {
  '@keyframes FileAttachmentUpload-wiggle': {
    '0%': {
      transform: 'scale(1.05) rotate(-1deg)',
    },
    '50%': {
      transform: 'scale(1.05) rotate(1deg)',
    },
    '100%': {
      transform: 'scale(1.05) rotate(-1deg)',
    },
  },
  '&.dragging': {
    animation: 'FileAttachmentUpload-wiggle 500ms infinite ease',
    bgcolor: (theme: Theme) => lighten(theme.palette.success.light, 0.9),
    borderColor: 'success.main',
  },
};

export type FileAttachmentCardProps = {
  name: string;
  onView: () => void;
  onDelete?: () => void;
};

export const FileAttachmentCard = ({ name, onView, onDelete }: FileAttachmentCardProps) => {
  const labelId = useId();
  const { formatMessage } = useIntl();
  return (
    <Card aria-labelledby={labelId} sx={CardSx}>
      <CardActionArea
        aria-label={formatMessage({ defaultMessage: 'View Attachment: {name}' }, { name })}
        onClick={onView}
      >
        <Box
          height={88}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="grey.100"
        >
          <FilePresent fontSize="large" />
        </Box>
      </CardActionArea>
      <CardContent sx={CardContentSx}>
        <Typography id={labelId} component="div" variant="body2" noWrap title={name}>
          {name}
        </Typography>
      </CardContent>
      <CardActions sx={{ p: 1 }}>
        <Button
          variant="text"
          color="primary"
          onClick={onDelete}
          aria-label={formatMessage({ defaultMessage: 'Delete Attachment: {name}' }, { name })}
        >
          <FormattedMessage defaultMessage="Delete Attachment" />
        </Button>
      </CardActions>
    </Card>
  );
};

export const FileAttachmentCardPlaceholder = () => (
  <Card aria-busy sx={CardSx}>
    <Box height={88} display="flex" alignItems="center" justifyContent="center" bgcolor="grey.100">
      <FilePresent fontSize="large" />
    </Box>
    <CardContent sx={CardContentSx}>
      <Typography component="div" variant="body2" noWrap>
        <Skeleton />
      </Typography>
    </CardContent>
    <CardActions sx={{ p: 1 }}>
      <Button variant="text" color="primary" disabled>
        <FormattedMessage defaultMessage="Delete Attachment" />
      </Button>
    </CardActions>
  </Card>
);

export type FileAttachmentCardErrorProps = { refetch: () => void };

export const FileAttachmentCardError = ({ refetch }: FileAttachmentCardErrorProps) => {
  const { formatMessage } = useIntl();
  return (
    <Card
      aria-busy
      sx={{
        borderColor: 'error.main',
        ...CardSx,
      }}
    >
      <Box
        height={88}
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor={(theme) => lighten(theme.palette.error.light, 0.9)}
      >
        <Error color="error" fontSize="large" />
      </Box>
      <CardContent sx={CardContentSx}>
        <Typography
          component="div"
          variant="body2"
          noWrap
          title={formatMessage({ defaultMessage: 'Error loading attachments' })}
        >
          <FormattedMessage defaultMessage="Error loading attachments" />
        </Typography>
      </CardContent>
      <CardActions sx={{ p: 1 }}>
        <Button variant="text" color="primary" onClick={refetch}>
          <FormattedMessage defaultMessage="Reload Attachments" />
        </Button>
      </CardActions>
    </Card>
  );
};

export type FileAttachmentUploadProps = {
  fileInputRef: MutableRefObject<HTMLInputElement>;
  validFileTypes: InputHTMLAttributes<HTMLInputElement>['accept'];
  handleAttachmentUpload: (event: ChangeEvent<HTMLInputElement>) => void;
  isMaxFilesUploaded: boolean;
  isUploadingAttachments: boolean;
  maxUploadsCount: number;
  prettyMaxFileSize: string;
};

export const FileAttachmentUpload = ({
  fileInputRef,
  validFileTypes,
  handleAttachmentUpload,
  isMaxFilesUploaded,
  isUploadingAttachments,
  maxUploadsCount,
  prettyMaxFileSize,
}: FileAttachmentUploadProps) => {
  const [dragCounter, setDragCounter] = useState(0);
  const onDragEnter = useCallback(() => setDragCounter((prev) => prev + 1), []);
  const onDragLeave = useCallback(() => setDragCounter((prev) => prev - 1), []);
  const onDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    // This how the DragDrop API works.
    // eslint-disable-next-line no-param-reassign
    event.dataTransfer.dropEffect = 'copy';
  }, []);
  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setDragCounter(0);
      const fakeEvent = {
        target: { files: event.dataTransfer.files },
      } as ChangeEvent<HTMLInputElement>;
      handleAttachmentUpload(fakeEvent);
    },
    [handleAttachmentUpload]
  );
  const onClickChooseFile = useCallback(() => fileInputRef.current?.click(), [fileInputRef]);
  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: 'primary.main',
        borderStyle: 'dashed',
        borderRadius: 2,
        bgcolor: (theme) => lighten(theme.palette.primary.light, 0.9),
        ...CardSx,
        ...FriendlyUploadCardAnimation,
      }}
      className={dragCounter > 0 ? 'dragging' : undefined}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <CardContent component={Stack} alignItems="center" gap={1} sx={CardContentSx}>
        <UploadFile color="primary" fontSize="large" />
        <Typography component="span" variant="subtitle1">
          <FormattedMessage defaultMessage="Drag & Drop a file" />
        </Typography>
        <LoadingButton
          disabled={isMaxFilesUploaded}
          loading={isUploadingAttachments}
          onClick={onClickChooseFile}
        >
          <FormattedMessage defaultMessage="Choose File" />
        </LoadingButton>
        <Typography
          component="span"
          variant="body2"
          color={isMaxFilesUploaded ? 'error' : undefined}
        >
          {isMaxFilesUploaded ? (
            <FormattedMessage
              defaultMessage="Maximum files ({max}) uploaded"
              values={{ max: maxUploadsCount }}
            />
          ) : (
            <FormattedMessage
              defaultMessage="(max. {maxFileSize})"
              values={{ maxFileSize: prettyMaxFileSize }}
            />
          )}
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept={validFileTypes}
          onChange={handleAttachmentUpload}
          style={{ display: 'none' }}
        />
      </CardContent>
    </Card>
  );
};
